import { useEffect, useRef, useState } from 'react'
import FloatingWindow from './FloatingWindow'
import RawRigBoard from './RawRigBoard'
import { rigBoardSource, subscribeRigBoard } from '../ogs/rig/rigBoardSource.js'
import {
  rigControlState, setRigChannel, setRigLock, setRigPose, resetRigControl,
  setRigDirect,
} from '../lib/rigControlState'
import { getCurrentArkit } from '../network/AudioAnimationBuffer'
import { initialRigPanelMode, nextRigPanelMode } from '../lib/rigPanelMode.js'
import { faceEvalResult } from '../lib/faceEvalResult'

/**
 * RigPreviewPanel — the face-control board (ENG-946).
 *
 * This panel is an INPUT DEVICE FIRST: it writes rigControlState, and where the
 * face blueprint wires a `rigController` node THAT node decides whether the
 * values reach the avatar, at what weight, blended against what.
 *
 * With no such node wired the panel drives the face itself (ENG-1027). The
 * ARKit->MetaHuman mapping is ours end to end — ogs/rig/arkitToMh.js into the
 * character's own control space via rigGatherFromNames — so a missing node
 * means no blueprint is authoring the routing, never that we don't know how the
 * sliders map. It arms on the FIRST slider or preset the user touches rather
 * than on open, so merely looking at the panel can't freeze a talking avatar,
 * and it says which of the two is driving. Precedence lives in one place:
 * rigControlState's rigControlWeight().
 *
 * Values live in the module singleton (read every frame by the render loop);
 * React state here mirrors them only for display, at UI cadence.
 */

// ARKit channels grouped into a MetaHuman-style control board. Derived once
// from the canonical channel order so the panel can never drift out of sync
// with the 52-slot contract.
const GROUP_DEFS = [
  { id: 'brow',   label: 'Brows',  match: n => n.startsWith('Brow') },
  { id: 'eye',    label: 'Eyes',   match: n => n.startsWith('Eye') },
  { id: 'cheek',  label: 'Cheeks', match: n => n.startsWith('Cheek') },
  { id: 'nose',   label: 'Nose',   match: n => n.startsWith('Nose') },
  { id: 'jaw',    label: 'Jaw',    match: n => n.startsWith('Jaw') },
  { id: 'mouth',  label: 'Mouth',  match: n => n.startsWith('Mouth') },
  { id: 'tongue', label: 'Tongue', match: n => n.startsWith('Tongue') },
]

function buildGroups(names) {
  const taken = new Set()
  const groups = GROUP_DEFS.map(def => {
    const channels = []
    names.forEach((name, index) => {
      if (taken.has(index) || !def.match(name)) return
      taken.add(index)
      channels.push({ index, name, label: prettyLabel(name, def) })
    })
    return { ...def, channels }
  }).filter(g => g.channels.length > 0)

  // Anything a group didn't claim still gets a home — a silently missing
  // slider would be worse than an ugly one.
  const rest = names
    .map((name, index) => ({ index, name, label: prettyLabel(name, null) }))
    .filter(c => !taken.has(c.index))
  if (rest.length) groups.push({ id: 'other', label: 'Other', channels: rest })
  return groups
}

/** 'MouthSmileLeft' -> 'Smile Left' (group prefix dropped, PascalCase split). */
function prettyLabel(name, def) {
  let s = name
  if (def) {
    const prefix = def.label.replace(/s$/, '') // Brows -> Brow, Eyes -> Eye
    if (s.startsWith(prefix)) s = s.slice(prefix.length) || name
  }
  return s.replace(/([a-z])([A-Z])/g, '$1 $2').trim()
}

// Same six poses as AudioAnimationBuffer's __FORCE_ARKIT__ presets, so the two
// debug tools speak the same vocabulary.
const PRESETS = {
  rest: {},
  aaah: { jawOpen: 0.9 },
  smile: { mouthSmileLeft: 0.7, mouthSmileRight: 0.7 },
  blink: { eyeBlinkLeft: 1.0, eyeBlinkRight: 1.0 },
  pucker: { mouthPucker: 0.8 },
  surprise: { jawOpen: 0.5, mouthFunnel: 0.3, eyeWideLeft: 0.6, eyeWideRight: 0.6 },
}

const GROUPS = buildGroups(rigControlState.names)

export default function RigPreviewPanel({ onClose }) {
  // Display mirror of rigControlState.arkit. The render loop reads the module
  // singleton; this array only drives the slider positions.
  const [values, setValues] = useState(() => Array.from(rigControlState.arkit))
  const [locks, setLocks] = useState(() => Array.from(rigControlState.locked))
  const [passthrough, setPassthrough] = useState(rigControlState.passthrough)
  const [nodeActive, setNodeActive] = useState(false)
  // Display mirror of rigControlState.direct — the render loop reads the
  // singleton; this only picks the status line and is set alongside it.
  const [direct, setDirect] = useState(rigControlState.direct)
  // 'arkit' — the 52 blendshape channels every character type understands.
  // 'raw'   — the loaded splat bundle's own MetaHuman control space.
  // Which one a splat character opens on, and why, is rigPanelMode.js.
  const [mode, setMode] = useState(() => initialRigPanelMode(!!rigBoardSource.names))
  const [hasRawBoard, setHasRawBoard] = useState(() => !!rigBoardSource.names)
  /** The user has used the mode switch — from then on their choice outranks a
   *  bundle arriving. */
  const chosenRef = useRef(false)
  useEffect(() => subscribeRigBoard(s => {
    setHasRawBoard(!!s.names)
    setMode(m => nextRigPanelMode(m, !!s.names, chosenRef.current))
  }), [])
  const valuesRef = useRef(values)
  valuesRef.current = values

  // Closing the panel hands the face back to the graph. Without this the last
  // slider pose would stay frozen on the avatar with no UI left to explain it.
  useEffect(() => () => setRigDirect(false), [])

  // Arm direct drive on the first thing the user actually touches. Called
  // unconditionally: a wired rigController node still outranks it inside
  // rigControlWeight, so there is no race against the 100ms nodeActive poll.
  function armDirect() {
    if (!rigControlState.direct) {
      setRigDirect(true)
      setDirect(true)
    }
  }

  // Poll at the debugState cadence (100ms): pick up the node's passthrough
  // flag + whether a rigController is actually live, and in passthrough mode
  // show the incoming stream on the sliders the user has NOT locked.
  useEffect(() => {
    const timer = setInterval(() => {
      const pt = rigControlState.passthrough
      setPassthrough(pt)
      setNodeActive((faceEvalResult.weights?.['__rigcontrol__'] ?? 0) > 0)
      if (!pt) return
      const live = getCurrentArkit()
      if (!live) return
      const next = valuesRef.current.slice()
      let changed = false
      for (let i = 0; i < 52; i++) {
        if (rigControlState.locked[i]) continue
        if (Math.abs(next[i] - live[i]) > 1e-3) { next[i] = live[i]; changed = true }
      }
      if (changed) setValues(next)
    }, 100)
    return () => clearInterval(timer)
  }, [])

  function applyChannel(index, value) {
    armDirect()
    setRigChannel(index, value)
    setValues(prev => { const next = prev.slice(); next[index] = value; return next })
    // Touching a slider while the stream is flowing means "I want this one" —
    // otherwise the next poll would wipe the value the user just dialled in.
    if (rigControlState.passthrough && !rigControlState.locked[index]) {
      setRigLock(index, true)
      setLocks(prev => { const next = prev.slice(); next[index] = 1; return next })
    }
  }

  function toggleLock(index) {
    const on = !rigControlState.locked[index]
    setRigLock(index, on)
    setLocks(prev => { const next = prev.slice(); next[index] = on ? 1 : 0; return next })
  }

  function applyPreset(name) {
    // resetRigControl() clears `direct` along with the values, so re-arm after
    // it rather than before — otherwise every preset click would disarm.
    resetRigControl()
    setRigDirect(true)
    setDirect(true)
    setRigPose(PRESETS[name])
    setValues(Array.from(rigControlState.arkit))
    setLocks(Array.from(rigControlState.locked))
  }

  function zeroAll() {
    resetRigControl()
    // Zeroing is still a pose the user asked for — stay armed so the face goes
    // to neutral instead of snapping back to whatever the graph was playing.
    setRigDirect(true)
    setDirect(true)
    setValues(Array.from(rigControlState.arkit))
    setLocks(Array.from(rigControlState.locked))
  }

  return (
    <FloatingWindow
      id="rig-preview"
      title="RIG PREVIEW"
      onClose={onClose}
      dockable={false}
      defaultSize={{ w: 360, h: 640 }}
      // You pose the face while watching it — centred, this window would open
      // directly on top of the avatar it exists to drive.
      defaultAnchor="right"
    >
      <div
        data-testid="rig-preview-panel"
        className="aos-thin-scroll"
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          background: 'var(--m-panel)', color: 'var(--m-fg)',
          padding: '12px 14px', fontFamily: 'var(--m-font)', fontSize: 11,
        }}
      >
        {/* Mode switch — only meaningful once a splat bundle has published a
            control space; a GLB character has no raw rig to address. */}
        {hasRawBoard && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {[['arkit', 'ARKit 52'], ['raw', `Rig ${rigBoardSource.dim}`]].map(([id, label]) => (
              <button
                key={id}
                type="button"
                data-testid={`rig-mode-${id}`}
                onClick={() => { chosenRef.current = true; setMode(id) }}
                style={{
                  ...btnStyle,
                  background: mode === id
                    ? 'color-mix(in srgb, var(--m-accent) 22%, transparent)'
                    : 'var(--m-panel-hi)',
                  color: mode === id ? 'var(--m-fg)' : 'var(--m-fg-2)',
                }}
              >{label}</button>
            ))}
            <span style={{ marginLeft: 'auto', color: 'var(--m-fg-3)', alignSelf: 'center' }}>
              {rigBoardSource.subject ?? ''}
            </span>
          </div>
        )}

        {mode === 'raw' ? <RawRigBoard /> : <>

        {/* Presets */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {Object.keys(PRESETS).map(name => (
            <button
              key={name}
              type="button"
              data-testid={`rig-preset-${name}`}
              onClick={() => applyPreset(name)}
              style={btnStyle}
            >{name}</button>
          ))}
          <button type="button" data-testid="rig-zero-all" onClick={zeroAll}
            style={{ ...btnStyle, marginLeft: 'auto' }}>zero all</button>
        </div>

        {/* Mode + wiring status */}
        <div
          data-testid="rig-preview-status"
          style={{
            marginBottom: 10, padding: '6px 8px', borderRadius: 4, lineHeight: 1.45,
            background: (nodeActive || direct)
              ? 'color-mix(in srgb, var(--m-accent) 12%, transparent)'
              : 'var(--m-panel-hi)',
            color: (nodeActive || direct) ? 'var(--m-fg-2)' : 'var(--m-fg-3)',
          }}
        >
          {nodeActive
            ? (passthrough
                ? 'Rig Controller live — A2F passthrough. Move a slider to lock that channel.'
                : 'Rig Controller live — slider pose.')
            : direct
              ? 'Driving the face directly — no Rig Controller node in the Face blueprint. Close this panel to hand the face back to the graph.'
              : 'No Rig Controller node in the Face blueprint. Move a slider and this panel will drive the face directly.'}
        </div>

        {/* Control board */}
        {GROUPS.map(group => (
          <details key={group.id} open data-testid={`rig-group-${group.id}`}
            style={{ marginBottom: 6 }}>
            <summary style={{
              cursor: 'pointer', color: 'var(--m-fg-3)', fontSize: 10,
              textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0',
            }}>{group.label}</summary>
            {group.channels.map(ch => (
              <div key={ch.name} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0' }}>
                {passthrough && (
                  <button
                    type="button"
                    title={locks[ch.index] ? 'Locked — slider overrides the stream' : 'Following the live stream'}
                    data-testid={`rig-lock-${ch.name}`}
                    onClick={() => toggleLock(ch.index)}
                    style={{
                      ...btnStyle, padding: '0 3px', minWidth: 18,
                      opacity: locks[ch.index] ? 1 : 0.35,
                    }}
                  >{locks[ch.index] ? '🔒' : '🔓'}</button>
                )}
                <label
                  title={ch.name}
                  style={{
                    flex: 'none', width: 96, color: 'var(--m-fg-2)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >{ch.label}</label>
                <input
                  type="range"
                  min="0" max="1" step="0.01"
                  value={values[ch.index]}
                  data-testid={`rig-slider-${ch.name}`}
                  onChange={e => applyChannel(ch.index, parseFloat(e.target.value))}
                  className={`aos-slider${locks[ch.index] ? ' aos-slider-locked' : ''}`}
                  style={{ flex: 1, minWidth: 0, '--aos-slider-fill': `${values[ch.index] * 100}%` }}
                />
                <span style={{
                  flex: 'none', width: 30, textAlign: 'right',
                  color: values[ch.index] > 0.001 ? 'var(--m-accent)' : 'var(--m-fg-3)',
                }}>{values[ch.index].toFixed(2)}</span>
              </div>
            ))}
          </details>
        ))}
        </>}
      </div>
    </FloatingWindow>
  )
}

const btnStyle = {
  font: 'inherit', fontSize: 10, cursor: 'pointer',
  padding: '2px 7px', borderRadius: 4,
  border: '1px solid var(--m-line)',
  background: 'var(--m-panel-hi)', color: 'var(--m-fg-2)',
}
